import { EnvironmentInjector, Injectable, inject, runInInjectionContext } from '@angular/core';

/**
 * Runs AngularFire compat reference-building inside an injection context.
 *
 * From @angular/fire 19 onward, `AngularFirestoreCollection` and `AngularFirestoreDocument`
 * initialise a field with `inject(EnvironmentInjector)`, and `AngularFirestore.collection()` /
 * `.doc()` construct them with a bare `new` — no `runInInjectionContext` of their own. A field
 * initializer only counts as an injection context while Angular is constructing the class, so any
 * call made from an ordinary async service method throws NG0203. That is angular/angularfire#3621,
 * and it is unfixed as of 20.0.1 (21 is still an RC).
 *
 * Every Firestore read in this app is a one-shot `.get()` from a method — none are built in a
 * constructor — so without this the splash never finishes, and banners, events, promotions,
 * notifications and push all fail.
 *
 * Wrap the WHOLE chain, not just the first call: `.doc()` off a collection constructs a second
 * wrapper, which needs the context just as much as the first.
 *
 *   const snap = await this.fsContext.run(() =>
 *     this.afs.collection('events', ref => ref.where(...)).get()
 *   ).toPromise();
 *
 * This is a workaround for a library bug, not a pattern to spread. If AngularFire fixes the compat
 * layer, every `fsContext.run(() => …)` here can collapse back to a direct call.
 */
@Injectable({ providedIn: 'root' })
export class FirestoreContextService {
  private readonly injector = inject(EnvironmentInjector);

  run<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }
}
